import React, { useEffect } from "react"
import { View } from "react-native"
import { connect } from "react-redux"
import { useNavigation, useRoute } from "@react-navigation/native"
import { ActivityIndicator } from "react-native-paper"
import * as amplitude from "@amplitude/analytics-react-native"
import TextM from "@common/components/text/TextM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { theme } from "@common/theme/theme"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { apiRequest } from "@data/redux/actions/api"
import { summerFunnelService } from "./services/summerFunnelService"
import { useSummerFunnel } from "./context/SummerFunnelContext"
import { readBoolParam, readUserParam } from "./hooks/useSummerFunnelGuard"
import {
    normaliseClass,
    deriveClassRentree,
    filterOfferedSlotIds,
    filterValidSlotIds,
} from "./constants/summerContent"

/**
 * Admin-issued magic link landing: GET /summer/resume?user=<base64email>.
 *
 * Calls the resume endpoint (behind `auth_or_email`), hydrates the funnel
 * Context with the persisted summer FastTrackState, then navigation.reset's to
 * the step matching the current state. Pendant du BrevetResumeScreen.
 *
 * No token, no expiration — the admin who shares the link already has BO
 * access; the URL is short-lived in practice (sent in a support call).
 */
// Map each state to the full stack history so navigation.goBack() walks back
// through the previous funnel steps natively. The LAST entry is the step the
// lead resumes on. Mirrors BrevetResumeScreen's intent, adapted to the summer
// steps: a state lands the parent on the next actionable screen.
//   summer_started               → diagnostic à compléter
//   summer_diag_completed        → configurateur (créneaux/mois/prix) à régler
//   summer_checkout_created      → créneaux + paiement à finaliser
//   summer_paid / _attribution_pending → confirmation (parent reconsulte)
//   summer_failed                → retour au checkout pour retenter le paiement
// Ordre du funnel (à plat) : start → diagnostic → configurator → plan (reveal)
// → slots-checkout. Le configurateur précède le reveal pour qu'il génère le vrai
// programme sur les créneaux choisis.
// `null` covers three cases (backend 1:N):
//   1) user has no FastTrackState at all (orphan / fresh student)
//   2) user has only non-summer rows (brevet_*, trial_done, …)
//   3) user HAS a post-payment summer row but has churned — the backend nulls the
//      state so they can buy again (SummerFunnelController::hasChurned)
// All three land on summer/start with hydrated user info from /resume.
const STATE_TO_STACK = {
    summer_started: ["summer/start", "summer/diagnostic"],
    summer_diag_completed: [
        "summer/start",
        "summer/diagnostic",
        "summer/configurator",
    ],
    summer_checkout_created: [
        "summer/start",
        "summer/diagnostic",
        "summer/configurator",
        "summer/plan",
        "summer/tutors",
        "summer/slots-checkout",
    ],
    summer_failed: [
        "summer/start",
        "summer/diagnostic",
        "summer/configurator",
        "summer/plan",
        "summer/tutors",
        "summer/slots-checkout",
    ],
    summer_paid: ["summer/confirmation"],
    summer_attribution_pending: ["summer/confirmation"],
    null: ["summer/start"],
}

const TERMINAL_PAID_STATES = ["summer_paid", "summer_attribution_pending"]

// COUTURE DE NORMALISATION UNIQUE du funnel été. Tout ce que les écrans aval
// consomment passe par ici : on borne les types et on backfille les champs
// manquants depuis la source la plus fiable, pour que la reprise marche à 100 %
// quel que soit l'âge/la complétude du checkout_params (anciens + nouveaux leads).
const mapStateCheckoutParamsToContext = (state, checkoutParams, user) => {
    const cp = checkoutParams || {}
    // Classe normalisée (ex. "3eme" → "3ème") : base de la dérivation rentrée ET
    // du chargement des matières. Sans normalisation, getSubjects renvoie le
    // mauvais/aucun catalogue → les matières reprises sont filtrées à vide.
    const studentClass =
        normaliseClass(cp.student_class || user?.student_class) || null
    // Créneaux : ne garder que les ids reconnus par le schéma courant ET encore EN
    // VENTE. Un id périmé/étranger ferait sous-compter les heures hebdo et
    // déclencherait un retrait auto de matières au 1er toggle ; un créneau saturé
    // (state d'avant sa fermeture) ferait 422 le programme.
    const offeredSlotIds = filterOfferedSlotIds(cp.slot_ids)
    // Rien en vente là-dedans : on distingue deux cas. Des créneaux catalogués mais
    // tous saturés → on blanchit, le parent re-choisit dans le configurateur (garder
    // la valeur brute mènerait droit au 422). Aucun id catalogué du tout (dérive de
    // schéma) → on conserve la valeur brute, comportement historique, les défauts
    // aval prennent le relais.
    const validSlotIds = offeredSlotIds.length
        ? offeredSlotIds
        : filterValidSlotIds(cp.slot_ids).length
          ? []
          : cp.slot_ids || []
    return {
        funnelStateId: state.funnel_state_id || null,
        // Le prénom LIVE (corrigé au BO) prime sur le snapshot figé du formulaire ;
        // fallback sur le snapshot tant qu'aucun compte n'existe encore.
        studentFirstname: user?.firstname || cp.firstname || "",
        parentEmail: user?.email || "",
        submissionGUID: cp.submission_guid || null,
        studentClass,
        // Rentrée dérivée de la classe quand le snapshot ne la porte pas (anciens
        // leads) — sinon hasProgramPayload reste faux et le programme + recap
        // matières sautent au checkout.
        studentClassRentree:
            cp.student_class_rentree || deriveClassRentree(studentClass) || null,
        // L'endpoint programme réécrit checkout_params['subjects'] en ENTIERS
        // (intval), alors que les chips matières comparent en string
        // (String(s.id) + .includes strict). On coerce en string dès l'hydratation
        // pour que les matières restaurées matchent les options et restent cochées
        // — sinon le configurateur les filtre toutes (Set de strings vs id entier).
        subjects: (cp.subjects || []).map(String),
        objective: cp.objective || null,
        niveauActuel: cp.niveau_actuel || null,
        selectedSlotIds: validSlotIds,
        selectedMonths: cp.months || [7, 8],
        // Répartition heures/matière reprise (sinon split auto + signature
        // programme différente de celle d'origine à la reprise).
        subjectHours: cp.subject_hours || {},
        // Date de démarrage choisie — re-validée en aval contre les options
        // vivantes (une reprise tardive retombe sur l'option la plus proche).
        startDate: cp.start_date || null,
        // Variante engagement + chemin sans dépôt : choisis par l'admin et portés
        // par checkout_params (le générateur de lien BO les pose). Hydratés ici pour
        // que le funnel les applique réellement (sinon défaut sans engagement / 1 €).
        withCommitment: !!cp.with_commitment,
        skipDeposit: !!cp.skip_deposit_payment,
        paid: TERMINAL_PAID_STATES.includes(state.state),
    }
}

const SummerResumeScreen = (props) => {
    const navigation = useNavigation()
    const route = useRoute()
    const { update, reset } = useSummerFunnel()

    const userParam = readUserParam(route)

    useEffect(() => {
        if (!userParam) return
        // Reset first so the Context doesn't keep stale values from a previous lead
        // sharing the same browser tab (admin-on-laptop flow).
        reset()
        props.apiRequest(
            summerFunnelService.resumeSummer,
            {},
            {},
            { user: userParam }
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userParam])

    useApiStatus(
        summerFunnelService.resumeSummer,
        null,
        false,
        (data) => {
            if (!data) return
            const stateName = data.state
            const ctx = mapStateCheckoutParamsToContext(
                data,
                data.checkout_params,
                data.user
            )
            // Lien fast-track fabriqué à la main (avant le générateur BO P-1971) :
            // un param d'URL explicite prime sur l'état serveur pour le choix
            // engagement / sans-dépôt. Absent → on garde la valeur de checkout_params.
            const engagementParam = readBoolParam(route, "engagement")
            const sansDepotParam = readBoolParam(route, "sans_depot")
            if (engagementParam !== null) ctx.withCommitment = engagementParam
            if (sansDepotParam !== null) ctx.skipDeposit = sansDepotParam
            update(ctx)

            // Track the magic-link "fresh start" branch — every state=null case
            // above, so it mixes "no summer row yet" with "churned family sent
            // back to a fresh run". Lets us measure how many admin links land on
            // an empty funnel vs a resumed one.
            if (stateName === null) {
                amplitude.track("summer_resume_fresh_start", {
                    has_user: !!data.user?.email,
                    has_student_class: !!data.user?.student_class,
                })
            }

            const stackKey = stateName === null ? "null" : stateName
            const stack = STATE_TO_STACK[stackKey] || ["summer/start"]
            // Carry `user` onto EVERY route of the rebuilt stack — not just the
            // landing step. The summer_checkout_created / summer_failed stacks now
            // include an intermediate `summer/tutors` step (aha-moment masteur): a
            // goBack() onto it — or a refresh while on it — must still find the
            // `?user=` token to stay shareable and refresh-resumable. With the param
            // only on the last route, every intermediate step lost the token.
            navigation.reset({
                index: stack.length - 1,
                routes: stack.map((name) => ({
                    name,
                    params: { user: userParam },
                })),
            })
        }
    )

    if (!userParam) {
        return (
            <View style={styles.container}>
                <TextM
                    color={theme.colors.error}
                    wrap
                    style={styles.errorText}
                    fontWeight="Bold"
                    fontSize={14}
                >
                    Lien invalide ou expiré
                </TextM>
                <FilledButtonM
                    onPress={() => navigation.replace("summer/start")}
                    label="Reprendre depuis le début"
                />
            </View>
        )
    }

    return (
        <View style={styles.container}>
            {props.error ? (
                <>
                    <TextM
                        color={theme.colors.error}
                        wrap
                        style={styles.errorText}
                        fontWeight="Bold"
                        fontSize={14}
                    >
                        Lien invalide ou expiré
                    </TextM>
                    <FilledButtonM
                        onPress={() => navigation.replace("summer/start")}
                        label="Reprendre depuis le début"
                    />
                </>
            ) : (
                <>
                    <ActivityIndicator
                        color={theme.colors.primary}
                        style={{ marginTop: 30 }}
                    />
                    <TextM wrap>Reprise du parcours en cours…</TextM>
                </>
            )}
        </View>
    )
}

const styles = {
    container: {
        alignItems: "center",
        flex: 1,
        padding: 20,
    },
    errorText: {
        marginTop: 20,
        marginBottom: 20,
        textAlign: "center",
    },
}

const mapStateToProps = (state) => ({
    error: state.api?.resumeSummer?.error,
})

const mapDispatchToProps = {
    apiRequest,
}

export default connect(mapStateToProps, mapDispatchToProps)(SummerResumeScreen)
